import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { expireClientSession, getAccessToken, getRefreshToken, storeSessionTokens } from './clientSession';

export const buildApiBaseUrl = (configuredBffUrl?: string) => {
  const normalizedBaseUrl = configuredBffUrl?.trim().replace(/\/+$/, '');

  if (normalizedBaseUrl) {
    return normalizedBaseUrl.endsWith('/api')
      ? normalizedBaseUrl
      : `${normalizedBaseUrl}/api`;
  }

  return 'http://localhost:5280/api';
};

const API_BASE_URL = buildApiBaseUrl(process.env.REACT_APP_BFF_URL);

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const authenticatedActivityListeners = new Set<() => void>();

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

const isRefreshRequest = (url?: string) => {
  return typeof url === 'string' && url.includes('/auth/refresh');
};

const notifyAuthenticatedActivity = () => {
  authenticatedActivityListeners.forEach((listener) => {
    listener();
  });
};

const wasAuthenticatedRequest = (config?: InternalAxiosRequestConfig) => {
  const authorizationHeader = config?.headers?.Authorization ?? config?.headers?.authorization;
  return typeof authorizationHeader === 'string' && authorizationHeader.startsWith('Bearer ');
};

export const shouldAttemptRefreshForUnauthorized = (config?: RetryableRequestConfig) => {
  if (!config) {
    return false;
  }

  if (!wasAuthenticatedRequest(config)) {
    return false;
  }

  if (isRefreshRequest(config.url) || config._retry) {
    return false;
  }

  return true;
};

const extractLoginResponse = (payload: any): LoginResponse | null => {
  if (!payload) {
    return null;
  }

  const responseData = payload.data ?? payload;
  if (!responseData || typeof responseData !== 'object') {
    return null;
  }

  return responseData as LoginResponse;
};

let refreshPromise: Promise<LoginResponse | null> | null = null;

const refreshSession = async (): Promise<LoginResponse | null> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  if (!refreshPromise) {
    refreshPromise = axios
      .post<LoginResponse | ApiResponse<LoginResponse>>(`${API_BASE_URL}/auth/refresh`, { refreshToken }, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then((response) => {
        const tokens = extractLoginResponse(response.data);
        if (!tokens?.accessToken) {
          return null;
        }

        storeSessionTokens({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        });

        return tokens;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

// Add bearer token to all requests
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Remove Content-Type header for FormData so axios can auto-detect multipart boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
}, (error) => Promise.reject(error));

// Handle responses and errors
api.interceptors.response.use(
  (response) => {
    if (wasAuthenticatedRequest(response.config) && !isRefreshRequest(response.config.url)) {
      notifyAuthenticatedActivity();
    }

    return response;
  },
  (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    if (!shouldAttemptRefreshForUnauthorized(originalRequest)) {
      if (isRefreshRequest(originalRequest.url) || originalRequest._retry) {
        expireClientSession('unauthorized');
      }

      return Promise.reject(error);
    }

    originalRequest._retry = true;

    return refreshSession()
      .then((tokens) => {
        if (!tokens?.accessToken) {
          expireClientSession('unauthorized');
          return Promise.reject(error);
        }

        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;

        return api(originalRequest);
      })
      .catch((refreshError) => {
        expireClientSession('unauthorized');
        return Promise.reject(refreshError);
      });
  }
);

export default api;

export const subscribeToAuthenticatedApiActivity = (listener: () => void) => {
  authenticatedActivityListeners.add(listener);

  return () => {
    authenticatedActivityListeners.delete(listener);
  };
};

// Types for API responses
export interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
  errors?: Record<string, string[]>;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  user?: UserDto;
}

export interface SessionConfigDto {
  idleTimeoutMinutes: number;
}

export interface UserDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  lastOpenedTreeId?: string;
  systemRole?: string;
  canAllowLivingPersonPublicDisplay?: boolean;
  preferredLocale?: string;
}

export interface FamilyTreeDto {
  id: string;
  name: string;
  description?: string;
  ownerId: string;
  createdAt: string;
  lastModified: string;
  role?: string;
  memberCount: number;
  canEdit: boolean;
  canDelete: boolean;
  canAddPerson: boolean;
  canAddRelationship?: boolean;
  canManageMembers?: boolean;
  canArchive?: boolean;
  canTransferOwnership?: boolean;
  canMoveNodes?: boolean;
}

export interface FamilyTreeDetailedDto extends FamilyTreeDto {
  people: PersonDto[];
  relationships: RelationshipDto[];
  members: TreeMemberDto[];
  layout?: CanvasLayout;
  canManageMembers?: boolean;
}

export interface FamilyTreeAccessDto {
  id: string;
  name: string;
  ownerId: string;
  role: string;
  canEdit?: boolean;
  canDelete?: boolean;
  canAddPerson?: boolean;
  canAddRelationship?: boolean;
  canManageMembers: boolean;
  canArchive?: boolean;
  canTransferOwnership?: boolean;
  canMoveNodes?: boolean;
}

export interface PersonDto {
  id: string;
  treeId: string;
  addedByUserId?: string;
  // Name components
  firstName: string;
  middleName?: string;
  lastName: string;
  title?: string;
  suffix?: string;
  // Demographics
  gender?: string;
  dateOfBirth?: string;
  dateOfDeath?: string;
  placeOfBirth?: string;
  placeOfDeath?: string;
  restingPlace?: string;
  // Address components
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  // Contact
  phone?: string;
  photoUrl?: string;
  // Bio
  shortBio?: string;
  shortDescription?: string;
  longStory?: string;
  comments?: string;
  // Canvas
  x?: number;
  y?: number;
  // Photo crop data
  photoCropX?: number;
  photoCropY?: number;
  photoCropWidth?: number;
  photoCropHeight?: number;
  photoCropZoom?: number;
  // Permissions
  canEdit: boolean;
  canDelete: boolean;
  // Computed
  fullName?: string;
  isDeceased?: boolean;
  publicVisibility?: 'Public' | 'Masked';
  allowPublicDisplayForLivingPerson?: boolean;
}

export interface LivingPersonPublicDisplayConsentRequest {
  accepted: boolean;
  consentMessageVersion: string;
  warningTextSnapshot: string;
  consentSource?: string;
  affectedPersonIds?: string[];
  affectedPersonNamesSnapshot?: string[];
}

const normalizePersonDto = (person: PersonDto): PersonDto => ({
  ...person,
  shortBio: person.shortBio ?? person.shortDescription ?? undefined,
});

const normalizeFamilyTreeDetailedDto = (tree: FamilyTreeDetailedDto): FamilyTreeDetailedDto => ({
  ...tree,
  people: Array.isArray(tree.people) ? tree.people.map(normalizePersonDto) : [],
});

export interface CreatePersonRequest {
  treeId: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  title?: string;
  suffix?: string;
  gender: string;
  dateOfBirth?: string;
  dateOfDeath?: string;
  placeOfBirth?: string;
  placeOfDeath?: string;
  restingPlace?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone?: string;
  photoUrl?: string;
  publicVisibility?: 'Public' | 'Masked';
  allowPublicDisplayForLivingPerson?: boolean;
  livingPersonPublicDisplayConsent?: LivingPersonPublicDisplayConsentRequest;
}

export interface UpdatePersonRequest {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  title?: string;
  suffix?: string;
  gender?: string;
  dateOfBirth?: string;
  clearDateOfBirth?: boolean;
  dateOfDeath?: string;
  clearDateOfDeath?: boolean;
  placeOfBirth?: string;
  placeOfDeath?: string;
  restingPlace?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone?: string;
  photoUrl?: string;
  shortDescription?: string;
  longStory?: string;
  comments?: string;
  isDeceased?: boolean;
  publicVisibility?: 'Public' | 'Masked';
  allowPublicDisplayForLivingPerson?: boolean;
  livingPersonPublicDisplayConsent?: LivingPersonPublicDisplayConsentRequest;
  x?: number;
  y?: number;
  photoCropX?: number | null;
  photoCropY?: number | null;
  photoCropWidth?: number | null;
  photoCropHeight?: number | null;
  photoCropZoom?: number | null;
}

export interface RelationshipDto {
  id: string;
  personAId: string;
  personBId: string;
  type: string;
  createdByUserId?: string;
  canEdit: boolean;
  canDelete: boolean;
}

export interface CreateRelationshipRequest {
  treeId: string;
  personAId: string;
  personBId: string;
  type: string;
}

export interface UpdateRelationshipRequest {
  type?: string;
}

export interface TreeMemberDto {
  id: string;
  treeId: string;
  userId: string;
  userEmail: string;
  role: 'Owner' | 'Manager' | 'Editor' | 'Contributor' | 'Viewer';
  joinedAt: string;
}

export interface InviteLinkDto {
  id: string;
  treeId: string;
  token: string;
  inviteeEmail: string;
  role: string;
  expiresAt: string;
  usedAt?: string;
  isRevoked?: boolean;
  revokedAt?: string;
  revokedByUserId?: string;
  canRevoke: boolean;
}

export interface InviteValidationDto {
  treeId: string;
  treeName: string;
  role: string;
  expiresAt: string;
}

export interface RedeemInviteLinkResponseDto {
  message: string;
  treeId: string;
  role: string;
}

export interface CanvasLayout {
  positions: Array<{
    personId: string;
    x: number;
    y: number;
  }>;
}

export interface CanvasViewportDto {
  zoomLevel: number;
  panX: number;
  panY: number;
}

export interface AiSessionContext {
  sessionId: string;
  createdAtUtc: string;
  lastAccessedAtUtc: string;
  sessionVersion: number;
  recentEntities: AiSessionEntityReference[];
  recentNavigationTargets: AiSessionNavigationTarget[];
  recentCommands: string[];
  pendingActionPlan?: AiPendingActionPlan | null;
  isCleared: boolean;
}

export interface AiSessionEntityReference {
  entityId: string;
  entityType: string;
  displayName: string;
}

export interface AiSessionNavigationTarget {
  entityId: string;
  entityType: string;
  displayName: string;
  highlightReason?: string | null;
}

export interface AiCreateSessionRequest {
  treeId: string;
  userId?: string;
  initialCommand?: string;
  correlationId?: string;
}

export interface AiPlannerRequest {
  requestId: string;
  sessionId: string;
  userInput: string;
  requestedAtUtc: string;
  correlationId?: string;
  treeId?: string;
  userId?: string;
  session?: AiSessionContext | null;
  availableTools?: Array<{
    toolName: string;
    displayName: string;
    kind: number;
    description?: string;
    requiresConfirmation: boolean;
    inputSchemaRef?: string;
    outputSchemaRef?: string;
  }>;
}

export interface AiClarificationRequest {
  prompt: string;
  question?: string | null;
  choices: string[];
  required: boolean;
}

export interface AiPlannerIssue {
  code: string;
  message: string;
  category: string;
  isRetryable: boolean;
}

export interface AiPlanMessage {
  code: string;
  message: string;
  severity: 'Info' | 'Warning' | 'Error';
}

export interface AiPlanStep {
  order: number;
  title: string;
  description?: string | null;
  toolCallId?: string | null;
  isBlocking: boolean;
}

export interface AiResolvedEntity {
  entityId: string;
  entityType: string;
  displayName: string;
  confidence: number;
  isExactMatch: boolean;
}

export interface AiPlannedToolCall {
  toolCallId: string;
  toolName: string;
  kind: number;
  arguments: Record<string, unknown>;
  requiresConfirmation: boolean;
}

export interface AiPendingActionPlan {
  planId: string;
  sessionId: string;
  status: 'Draft' | 'ReadyForConfirmation' | 'Confirmed' | 'Committed' | 'Expired' | 'Rejected';
  actionCategory: string;
  title: string;
  summary: string;
  createdAtUtc: string;
  expiresAtUtc: string;
  steps: AiPlanStep[];
  warnings: AiPlanMessage[];
  resolvedEntities: AiResolvedEntity[];
  plannedToolCalls: AiPlannedToolCall[];
}

export interface AiPlanConfirmationRequest {
  planId: string;
  commitNote?: string | null;
  correlationId?: string | null;
}

export interface AiPlanCommitResponse {
  sessionId: string;
  planId: string;
  status: string;
  actionCategory: string;
  summary?: string | null;
  warnings: AiPlanMessage[];
  errors: AiPlannerIssue[];
  committedAtUtc?: string | null;
  committedEntityId?: string | null;
  committedEntityType?: string | null;
  committedToolName?: string | null;
  updatedSession?: AiSessionContext | null;
}

export interface AiPlannerResponse {
  requestId: string;
  sessionId: string;
  status: string;
  actionCategory: string;
  summary?: string;
  clarification?: AiClarificationRequest | null;
  pendingActionPlan?: AiPendingActionPlan | null;
  readResult?: AiReadResult | null;
  warnings: AiPlanMessage[];
  errors: AiPlannerIssue[];
  updatedSession?: AiSessionContext | null;
}

export interface AiReadResult {
  query: string;
  mode: string;
  summary: string;
  subject?: AiResolvedEntity | null;
  items: AiResolvedEntity[];
  focusEntityId?: string | null;
}

export interface AiGraphResolvePersonRequest {
  treeId: string;
  query: string;
}

export interface AiGraphResolvePersonResponse {
  treeId: string;
  query: string;
  match?: PersonDto | null;
  alternatives: PersonDto[];
  isAmbiguous: boolean;
}

export interface AiGraphSearchPeopleResponse {
  treeId: string;
  query: string;
  results: PersonDto[];
}

export interface AiGraphTraversalResponse {
  treeId: string;
  personId: string;
  person?: PersonDto | null;
  direction: string;
  depth: number;
  relatedPeople: PersonDto[];
}

export interface AiGraphRelationshipPathResponse {
  treeId: string;
  fromPersonId: string;
  toPersonId: string;
  found: boolean;
  path: PersonDto[];
  steps: Array<{
    fromPersonId: string;
    toPersonId: string;
    relationshipType: string;
  }>;
}

export interface AiGraphFocusResponse {
  treeId: string;
  person?: PersonDto | null;
  viewport: CanvasViewportDto;
}

export interface AiGraphTreeStatsResponse {
  treeId: string;
  peopleCount: number;
  relationshipCount: number;
  directedEdgeCount: number;
  rootCandidateCount: number;
  leafCandidateCount: number;
  hasTree: boolean;
}

export interface AiConfigResponse {
  enabled: boolean;
}

export interface PublicTreeLinkAdminDto {
  id: string;
  treeId: string;
  name: string;
  slug?: string | null;
  tokenPrefix: string;
  isActive: boolean;
  allowComments: boolean;
  allowEmbedding: boolean;
  showLastName: boolean;
  showPhoto: boolean;
  createdAt: string;
  createdByUserId: string;
  disabledAt?: string | null;
  lastAccessedAt?: string | null;
  totalViews: number;
  pendingCommentsCount: number;
}

export interface PublicTreeLinkTokenResultDto {
  link: PublicTreeLinkAdminDto;
  token: string;
  urlPath: string;
  containsPublicLivingPeople: boolean;
}

export interface PublishedTreeVersionSummaryDto {
  id: string;
  treeId: string;
  versionNumber: number;
  publishNote?: string | null;
  createdAt: string;
  createdByUserId: string;
  containsPublicLivingPeople: boolean;
  livingPeopleCount: number;
}

export interface LivingPersonPublicDisplayCapabilityDto {
  systemAllowsLivingPersonPublicDisplay: boolean;
  userCanAllowLivingPersonPublicDisplay: boolean;
  userTreeRole?: string | null;
  canEnableLivingPersonPublicDisplay: boolean;
  blockedReason?: string | null;
}

export interface LivingPersonImpactPersonDto {
  personId: string;
  displayName: string;
}

export interface LivingPersonPublishPreflightDto {
  requiresLivingPersonPublicDisplayConsent: boolean;
  affectedLivingPeople: LivingPersonImpactPersonDto[];
  warningTitle?: string | null;
  warningMessage?: string | null;
}

export interface LivingPersonPublicLinkPreflightDto {
  requiresLivingPersonPublicDisplayConsent: boolean;
  latestPublishedVersionId?: string | null;
  affectedLivingPeopleCount: number;
  affectedLivingPeople: LivingPersonImpactPersonDto[];
  warningTitle?: string | null;
  warningMessage?: string | null;
}

export interface PublishTreeRequest {
  publishNote?: string;
  livingPersonPublicDisplayConsent?: LivingPersonPublicDisplayConsentRequest;
}

export interface CreatePublicTreeLinkRequest {
  name: string;
  slug?: string | null;
  isActive?: boolean;
  allowComments?: boolean;
  allowEmbedding?: boolean;
  showLastName?: boolean;
  showPhoto?: boolean;
  livingPersonPublicDisplayConsent?: LivingPersonPublicDisplayConsentRequest;
}

export interface RegeneratePublicTreeLinkTokenRequest {
  livingPersonPublicDisplayConsent?: LivingPersonPublicDisplayConsentRequest;
}

export interface AdminPrivacySettingsDto {
  allowLivingPersonPublicDisplay: boolean;
  updatedByUserId?: string | null;
  updatedAt: string;
}

export interface AdminUserPrivacyPermissionsDto {
  userId: string;
  email: string;
  fullName: string;
  systemRole: string;
  canAllowLivingPersonPublicDisplay: boolean;
  updatedAt?: string | null;
}

export interface PublicTreePersonDto {
  id: string;
  isMasked: boolean;
  displayName: string;
  firstName?: string | null;
  lastName?: string | null;
  shortDescription?: string | null;
  longDescription?: string | null;
  photoUrl?: string | null;
  gender?: string | null;
  dateOfBirth?: string | null;
  dateOfDeath?: string | null;
  x?: number | null;
  y?: number | null;
}

export interface PublicTreeRelationshipDto {
  id: string;
  sourcePersonId: string;
  targetPersonId: string;
  relationshipType: string;
}

export interface PublicCommentViewDto {
  id: string;
  targetType: 'Tree' | 'Person' | 'Relationship';
  targetId?: string | null;
  parentCommentId?: string | null;
  authorName: string;
  content: string;
  createdAt: string;
  replies: PublicCommentViewDto[];
}

export interface PublicTreeViewDto {
  treeId: string;
  publicLinkId: string;
  treeName: string;
  treeDescription?: string | null;
  versionNumber: number;
  publishedAt: string;
  allowComments: boolean;
  allowEmbedding: boolean;
  slug?: string | null;
  people: PublicTreePersonDto[];
  relationships: PublicTreeRelationshipDto[];
  comments: PublicCommentViewDto[];
}

export interface PublicCommentAdminDto {
  id: string;
  treeId: string;
  publicTreeLinkId: string;
  targetType: 'Tree' | 'Person' | 'Relationship';
  targetId?: string | null;
  parentCommentId?: string | null;
  authorName: string;
  authorEmail: string;
  authorPhone?: string | null;
  content: string;
  status: 'Pending' | 'Approved' | 'Rejected' | 'Deleted';
  createdAt: string;
  moderatedAt?: string | null;
  moderatedByUserId?: string | null;
}

export interface PublicLinkAnalyticsDto {
  publicLinkId: string;
  publicLinkName: string;
  range: '7d' | '30d' | '90d' | 'all' | string;
  totalViews: number;
  viewsLast7Days: number;
  viewsLast30Days: number;
  uniqueVisitors: number;
  mostActiveDay?: string | null;
  mostActiveDayViews: number;
  lastAccessedAt?: string | null;
  topReferrers: Array<{ domain: string; views: number }>;
  dailyViews: Array<{ date: string; views: number }>;
}

// API Methods
export interface ForgotPasswordRequest {
  emailOrPhone: string;
  method: 'Email' | 'Sms';
}

export interface VerifyOtpRequest {
  emailOrPhone: string;
  otpCode: string;
  newPassword: string;
}

export const authAPI = {
  getSessionConfig: () =>
    api.get<ApiResponse<SessionConfigDto>>('/auth/session-config'),

  login: (data: LoginRequest) =>
    api.post<ApiResponse<LoginResponse>>('/auth/login', data),

  register: (data: { email: string; password: string; firstName: string; lastName: string; phone?: string }) =>
    api.post<ApiResponse<LoginResponse>>('/auth/register', data),

  logout: () =>
    api.post('/auth/logout'),

  refresh: (refreshToken: string) =>
    api.post<LoginResponse | ApiResponse<LoginResponse>>('/auth/refresh', { refreshToken }),

  forgotPassword: (data: ForgotPasswordRequest) =>
    api.post('/auth/forgot-password', data),

  verifyOtp: (data: VerifyOtpRequest) =>
    api.post('/auth/verify-otp', data),
};

export const familyTreesAPI = {
  getOwnedTrees: () =>
    api.get<ApiResponse<FamilyTreeDto[]>>('/familytrees/owned'),

  getSharedTrees: () =>
    api.get<ApiResponse<FamilyTreeDto[]>>('/familytrees/shared'),

  getTree: (id: string) =>
    api.get<ApiResponse<FamilyTreeDetailedDto>>(`/familytrees/${id}`).then((response) => {
      if (response.data?.data) {
        response.data.data = normalizeFamilyTreeDetailedDto(response.data.data);
      }
      return response;
    }),

  getTreeAccess: (id: string) =>
    api.get<ApiResponse<FamilyTreeAccessDto>>(`/familytrees/${id}/access`),

  createTree: (data: { name: string; description?: string }) =>
    api.post<ApiResponse<FamilyTreeDto>>('/familytrees', data),

  updateTree: (id: string, data: { name: string; description?: string }) =>
    api.put<ApiResponse<FamilyTreeDto>>(`/familytrees/${id}`, data),

  deleteTree: (id: string) =>
    api.delete(`/familytrees/${id}`),

  archiveTree: (id: string) =>
    api.post(`/familytrees/${id}/archive`),

  transferOwnership: (id: string, newOwnerUserId: string) =>
    api.post(`/familytrees/${id}/transfer-ownership`, { newOwnerUserId }),

  saveLayout: (id: string, layout: CanvasLayout) =>
    api.put(`/familytrees/${id}/layout`, layout),

  getCanvasViewport: (treeId: string) =>
    api.get<ApiResponse<CanvasViewportDto>>(`/familytrees/${treeId}/viewport`),

  saveCanvasViewport: (treeId: string, viewport: CanvasViewportDto) =>
    api.put<ApiResponse<CanvasViewportDto>>(`/familytrees/${treeId}/viewport`, viewport),

  publishTree: (treeId: string, data: PublishTreeRequest) =>
    api.post<PublishedTreeVersionSummaryDto>(`/familytrees/${treeId}/publish`, data),

  getPublishedVersions: (treeId: string) =>
    api.get<PublishedTreeVersionSummaryDto[]>(`/familytrees/${treeId}/published-versions`),

  getLivingPersonPublicDisplayCapability: (treeId: string) =>
    api.get<LivingPersonPublicDisplayCapabilityDto>(`/familytrees/${treeId}/privacy/living-person-public-display-capability`),

  getPublishPreflight: (treeId: string) =>
    api.get<LivingPersonPublishPreflightDto>(`/familytrees/${treeId}/publish/preflight`),

  getPublicLinkPreflight: (treeId: string) =>
    api.get<LivingPersonPublicLinkPreflightDto>(`/familytrees/${treeId}/public-links/preflight`),
};

export const publicTreesAPI = {
  getPublicTreeByToken: (token: string) =>
    api.get<PublicTreeViewDto>(`/public-trees/${token}`),

  getPublicTreeBySlugAndToken: (slug: string, token: string) =>
    api.get<PublicTreeViewDto>(`/public-trees/${slug}/${token}`),

  createCommentByToken: (token: string, data: any) =>
    api.post(`/public-trees/${token}/comments`, data),

  createCommentBySlugAndToken: (slug: string, token: string, data: any) =>
    api.post(`/public-trees/${slug}/${token}/comments`, data),
};

export const publicTreeAdminAPI = {
  getLinks: (treeId: string) =>
    api.get<PublicTreeLinkAdminDto[]>(`/familytrees/${treeId}/public-links`),

  createLink: (treeId: string, data: CreatePublicTreeLinkRequest) =>
    api.post<PublicTreeLinkTokenResultDto>(`/familytrees/${treeId}/public-links`, data),

  updateLink: (publicLinkId: string, data: any) =>
    api.patch<PublicTreeLinkAdminDto>(`/public-links/${publicLinkId}`, data),

  regenerateLinkToken: (publicLinkId: string, data: RegeneratePublicTreeLinkTokenRequest = {}) =>
    api.post<PublicTreeLinkTokenResultDto>(`/public-links/${publicLinkId}/regenerate-token`, data),

  deleteLink: (publicLinkId: string) =>
    api.delete(`/public-links/${publicLinkId}`),

  getModerationQueue: (treeId: string) =>
    api.get<PublicCommentAdminDto[]>(`/familytrees/${treeId}/public-comments`, {
      params: {
        status: 'Pending',
      },
    }),

  moderateComment: (commentId: string, status: PublicCommentAdminDto['status']) =>
    api.patch<PublicCommentAdminDto>(`/public-comments/${commentId}`, { status }),

  deleteComment: (commentId: string) =>
    api.delete(`/public-comments/${commentId}`),

  getAnalytics: (publicLinkId: string, range = '7d') =>
    api.get<PublicLinkAnalyticsDto>(`/public-links/${publicLinkId}/analytics`, {
      params: {
        range,
      },
    }),
};

export const peopleAPI = {
  getPerson: (id: string) =>
    api.get<ApiResponse<PersonDto>>(`/people/${id}`).then((response) => {
      if (response.data?.data) {
        response.data.data = normalizePersonDto(response.data.data);
      }
      return response;
    }),

  createPerson: (data: CreatePersonRequest) =>
    api.post<ApiResponse<PersonDto>>('/people', data).then((response) => {
      if (response.data?.data) {
        response.data.data = normalizePersonDto(response.data.data);
      }
      return response;
    }),

  updatePerson: (id: string, data: UpdatePersonRequest) =>
    api.put<ApiResponse<PersonDto>>(`/people/${id}`, data).then((response) => {
      if (response.data?.data) {
        response.data.data = normalizePersonDto(response.data.data);
      }
      return response;
    }),

  deletePerson: (id: string) =>
    api.delete(`/people/${id}`),

  uploadPersonPhoto: (id: string, formData: FormData) =>
    api.post<ApiResponse<string>>(`/people/${id}/photo`, formData),
};

export const relationshipsAPI = {
  getRelationship: (id: string) =>
    api.get<ApiResponse<RelationshipDto>>(`/relationships/${id}`),

  createRelationship: (data: CreateRelationshipRequest) =>
    api.post<ApiResponse<RelationshipDto>>('/relationships', data),

  updateRelationship: (id: string, data: UpdateRelationshipRequest) =>
    api.put<ApiResponse<RelationshipDto>>(`/relationships/${id}`, data),

  deleteRelationship: (id: string) =>
    api.delete(`/relationships/${id}`),
};

export const treeMembersAPI = {
  getMembers: (treeId: string) =>
    api.get<TreeMemberDto[] | ApiResponse<TreeMemberDto[]>>(`/treemembers/tree/${treeId}`),

  addMember: (data: { treeId: string; userId: string; role: string }) =>
    api.post<ApiResponse<TreeMemberDto>>('/treemembers', data),

  updateMemberRole: (id: string, role: string) =>
    api.put<ApiResponse<TreeMemberDto>>(`/treemembers/${id}`, { role }),

  removeMember: (id: string) =>
    api.delete(`/treemembers/${id}`),
};

export const inviteLinksAPI = {
  getInviteLinks: (treeId: string, filters?: { status?: string; email?: string }) =>
    api.get<InviteLinkDto[] | ApiResponse<InviteLinkDto[]>>(`/invitelinks/tree/${treeId}`, {
      params: {
        status: filters?.status,
        email: filters?.email,
      },
    }),

  createInviteLink: (data: { treeId: string; inviteeEmail: string; role: string; expireDays: number }) =>
    api.post<InviteLinkDto | ApiResponse<InviteLinkDto>>('/invitelinks', data),

  validateInviteLink: (token: string) =>
    api.get<InviteValidationDto | ApiResponse<InviteValidationDto>>(`/invitelinks/validate/${token}`),

  redeemInviteLink: (token: string) =>
    api.post<RedeemInviteLinkResponseDto | ApiResponse<RedeemInviteLinkResponseDto>>('/invitelinks/redeem', { token }),

  revokeInviteLink: (id: string) =>
    api.post(`/invitelinks/${id}/revoke`),

  resendInviteLink: (id: string) =>
    api.post<InviteLinkDto | ApiResponse<InviteLinkDto>>(`/invitelinks/${id}/resend`, {}),
};

export const usersAPI = {
  getProfile: () =>
    api.get<ApiResponse<UserDto>>('/users/me'),

  updateProfile: (data: Partial<UserDto>) =>
    api.put<ApiResponse<UserDto>>('/users/me', data),

  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post('/users/me/password', data),

  deleteAccount: () =>
    api.delete('/users/me'),
};

export const adminPrivacyAPI = {
  getSettings: () =>
    api.get<AdminPrivacySettingsDto>('/admin/privacy-settings'),

  updateSettings: (data: Partial<AdminPrivacySettingsDto>) =>
    api.patch<AdminPrivacySettingsDto>('/admin/privacy-settings', data),

  getUserPermissions: (userId: string) =>
    api.get<AdminUserPrivacyPermissionsDto>(`/admin/users/${userId}/privacy-permissions`),

  searchUserPermissions: (query?: string, limit = 20) =>
    api.get<AdminUserPrivacyPermissionsDto[]>('/admin/users/privacy-permissions', {
      params: {
        query: query?.trim() || undefined,
        limit,
      },
    }),

  updateUserPermissions: (userId: string, data: Partial<AdminUserPrivacyPermissionsDto>) =>
    api.patch<AdminUserPrivacyPermissionsDto>(`/admin/users/${userId}/privacy-permissions`, data),
};

export const aiAPI = {
  getConfig: () =>
    api.get<AiConfigResponse>('/ai/config'),

  createSession: (data: AiCreateSessionRequest) =>
    api.post<AiSessionContext>('/ai/sessions', data),

  getSession: (sessionId: string) =>
    api.get<AiSessionContext>(`/ai/sessions/${sessionId}`),

  resetSession: (sessionId: string) =>
    api.delete<AiSessionContext>(`/ai/sessions/${sessionId}`),

  confirmPlan: (sessionId: string, data: AiPlanConfirmationRequest) =>
    api.post<AiPlanCommitResponse>(`/ai/sessions/${sessionId}/confirm-plan`, data),

  plan: (data: AiPlannerRequest) =>
    api.post<AiPlannerResponse>('/ai/planner', data),

  resolvePerson: (treeId: string, query: string) =>
    api.get<AiGraphResolvePersonResponse>('/ai/graph/resolve-person', {
      params: { treeId, query },
    }),

  searchPeople: (treeId: string, query: string, limit = 10) =>
    api.get<AiGraphSearchPeopleResponse>('/ai/graph/search-persons', {
      params: { treeId, query, limit },
    }),

  getAncestors: (treeId: string, personId: string, depth = 3) =>
    api.get<AiGraphTraversalResponse>('/ai/graph/ancestors', {
      params: { treeId, personId, depth },
    }),

  getDescendants: (treeId: string, personId: string, depth = 3) =>
    api.get<AiGraphTraversalResponse>('/ai/graph/descendants', {
      params: { treeId, personId, depth },
    }),

  getRelationshipPath: (treeId: string, fromPersonId: string, toPersonId: string) =>
    api.get<AiGraphRelationshipPathResponse>('/ai/graph/relationship-path', {
      params: { treeId, fromPersonId, toPersonId },
    }),

  getFocus: (treeId: string, personId: string) =>
    api.get<AiGraphFocusResponse>('/ai/graph/focus', {
      params: { treeId, personId },
    }),

  getTreeStats: (treeId: string) =>
    api.get<AiGraphTreeStatsResponse>('/ai/graph/stats', {
      params: { treeId },
    }),
};
